import { makeStyles, Typography, Button } from '@material-ui/core';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { NoDataItem } from '~/modules/common/components';
import { DimensionObjectType } from '~/types';
import ViewSummaryButton from './ViewSummaryButton';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: theme.spacing(1, 0, 0, 1)
  },
  rateSets: {
    fontWeight: theme.typography.fontWeightMedium,
    marginTop: theme.spacing(-0.2)
  },
  definedByDimensions: { marginTop: theme.spacing(-0.15) },
  checkForMissingRolesButton: {
    marginLeft: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0.25, 1, 0.25, 1)
  }
}));

export const AdvancedRateCardReadOnlyContent = ({
  projectRateCount,
  projectDimensions
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const { rateCount, dimensions } = useMemo(
    () => ({
      rateCount: { count: projectRateCount },
      dimensions: {
        dimensions:
          projectDimensions
            .map(d => d.displayText)
            .join(formatMessage({ id: 'advancedRateCard.separator' })) ||
          formatMessage({ id: 'advancedRateCard.defaultRate' })
      }
    }),
    [formatMessage, projectDimensions, projectRateCount]
  );

  return (
    <div className={classes.root}>
      <Typography
        variant="body2"
        className={classes.rateSets}
        data-qe-id="RateSet"
      >
        <FormattedMessage
          id={
            rateCount.count === 1
              ? 'advancedRateCard.rateSet'
              : 'advancedRateCard.ratesSet'
          }
          values={rateCount}
        />
      </Typography>
      &nbsp; &ndash; &nbsp;
      <Typography
        variant="body2"
        className={classes.definedByDimensions}
        data-qe-id="DefinedByDimensions"
      >
        <FormattedMessage
          id="advancedRateCard.definedByDimensions"
          values={dimensions}
        />
      </Typography>
    </div>
  );
};

AdvancedRateCardReadOnlyContent.propTypes = {
  projectRateCount: PropTypes.number,
  projectDimensions: PropTypes.array
};

export const AdvancedRateCardReadOnlyContent2 = ({
  projectRateCount,
  projectDimensions,
  projectRates,
  onEdit,
  onViewSummary,
  setShowMissingRoles,
  canEditBillingRates,
  editable
}) => {
  const classes = useStyles();
  const onClick = useCallback(() => {
    setShowMissingRoles(true);
    onEdit();
  }, [onEdit, setShowMissingRoles]);

  const projectRoleDimensionIndex = projectDimensions.findIndex(
    ({ objectType }) => objectType === DimensionObjectType.ProjectRole
  );

  return (
    <>
      {projectRates?.length ? (
        <>
          {editable && canEditBillingRates && projectRoleDimensionIndex !== -1 && (
            <Button
              className={classes.checkForMissingRolesButton}
              onClick={onClick}
              variant="outlined"
            >
              <FormattedMessage id="advancedRateCard.notReferencedProjectRoles.checkForMissingRoles" />
            </Button>
          )}
          <AdvancedRateCardReadOnlyContent
            projectRateCount={projectRateCount}
            projectDimensions={projectDimensions}
          />
          <ViewSummaryButton onViewSummaryClick={onViewSummary} />
        </>
      ) : (
        <div>
          <NoDataItem dataQeId="RateCard">
            <FormattedMessage id="advancedRateCard.noRates" />
          </NoDataItem>
        </div>
      )}
    </>
  );
};

AdvancedRateCardReadOnlyContent2.propTypes = {
  projectRateCount: PropTypes.number,
  projectDimensions: PropTypes.array,
  projectRates: PropTypes.array,
  onEdit: PropTypes.func,
  onViewSummary: PropTypes.func,
  setShowMissingRoles: PropTypes.func,
  canEditBillingRates: PropTypes.bool,
  editable: PropTypes.bool
};

export default AdvancedRateCardReadOnlyContent;
